/* Reusable Di Antonio campaign hero controls and GA4 promotion dataLayer events. */
(() => {
  const viewedSlides = (window.__diAntonioCampaignHeroViewedSlides ||= new Set());

  class DiAntonioCampaignHero extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (!this.initialized) return;

      this.stopAutoplay();
      this.finishTransition();
      this.observer?.disconnect();
      this.motionQuery?.removeEventListener?.('change', this.onMotionChange);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.pauseButton?.removeEventListener('click', this.onPause);
      this.controls.forEach((control, index) => control.removeEventListener('click', this.paginationHandlers[index]));
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('click', this.onCtaClick);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.initialized = false;
    }

    initialize() {
      if (this.initialized) return;

      this.slides = Array.from(this.querySelectorAll('[data-campaign-slide]'));
      this.controls = Array.from(this.querySelectorAll('[data-campaign-pagination]'));
      this.previousButton = this.querySelector('[data-campaign-previous]');
      this.nextButton = this.querySelector('[data-campaign-next]');
      this.pauseButton = this.querySelector('[data-campaign-pause]');
      this.pauseLabel = this.querySelector('[data-campaign-pause-label]');
      this.activeIndex = Math.max(this.slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.duration = Number(this.dataset.duration) || 6000;
      this.manuallyPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.isVisible = false;
      this.hasRendered = false;
      this.initialized = true;

      this.onPrevious = () => this.setActive(this.activeIndex - 1);
      this.onNext = () => this.setActive(this.activeIndex + 1);
      this.onPause = () => this.togglePause();
      this.onPointerEnter = () => {
        this.hoverPaused = true;
        this.stopAutoplay();
      };
      this.onPointerLeave = () => {
        this.hoverPaused = false;
        this.startAutoplay();
      };
      this.onFocusIn = () => {
        this.focusPaused = true;
        this.stopAutoplay();
      };
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.focusPaused = false;
          this.startAutoplay();
        }
      };
      this.onCtaClick = (event) => {
        const cta = event.target.closest('[data-campaign-cta]');
        if (!cta || cta.getAttribute('aria-disabled') === 'true') return;
        const slide = cta.closest('[data-campaign-slide]');
        if (slide) this.pushPromotion('select_promotion', slide, cta.dataset.promotionDestination);
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.stopAutoplay();
        else {
          if (this.isVisible) this.trackActiveSlide();
          this.startAutoplay();
        }
      };
      this.onMotionChange = () => {
        if (this.prefersReducedMotion()) this.stopAutoplay();
        else this.startAutoplay();
      };

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.pauseButton?.addEventListener('click', this.onPause);
      this.paginationHandlers = this.controls.map((control) => () => {
        this.setActive(Number(control.dataset.campaignPagination));
      });
      this.controls.forEach((control, index) => control.addEventListener('click', this.paginationHandlers[index]));
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('click', this.onCtaClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionQuery.addEventListener?.('change', this.onMotionChange);
      this.observeVisibility();
      this.setActive(this.activeIndex, false);
      this.hasRendered = true;
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        this.trackActiveSlide();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
          if (this.isVisible) this.trackActiveSlide();
        },
        { threshold: 0 }
      );
      this.observer.observe(this);
    }

    setActive(index, animate = this.hasRendered) {
      if (!this.slides.length) return;

      const nextIndex = (index + this.slides.length) % this.slides.length;
      const previousIndex = this.activeIndex;
      const previousSlide = this.slides[previousIndex];
      const nextSlide = this.slides[nextIndex];
      const shouldAnimate = animate && nextIndex !== previousIndex && !this.prefersReducedMotion();

      this.finishTransition();
      this.activeIndex = nextIndex;
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      if (shouldAnimate) {
        previousSlide.hidden = false;
        previousSlide.classList.add('is-leaving');
        nextSlide.hidden = false;
        nextSlide.classList.add('is-entering');

        this.transitionFrame = window.requestAnimationFrame(() => {
          this.transitionFrame = window.requestAnimationFrame(() => {
            previousSlide.classList.add('is-transitioning');
            nextSlide.classList.add('is-transitioning');
          });
        });

        this.transitionTimer = window.setTimeout(() => this.finishTransition(), 950);
      }

      this.controls.forEach((control, controlIndex) => {
        const isActive = controlIndex === nextIndex;
        control.classList.toggle('is-active', isActive);
        control.setAttribute('aria-current', String(isActive));
      });

      this.trackActiveSlide();
      this.updatePauseControl();
      this.stopAutoplay();
      this.startAutoplay();
    }

    finishTransition() {
      if (this.transitionFrame) {
        window.cancelAnimationFrame(this.transitionFrame);
        this.transitionFrame = null;
      }
      if (this.transitionTimer) {
        window.clearTimeout(this.transitionTimer);
        this.transitionTimer = null;
      }
      if (!this.slides) return;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.activeIndex;
        slide.classList.remove('is-entering', 'is-leaving', 'is-transitioning');
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
    }

    togglePause() {
      this.manuallyPaused = !this.manuallyPaused;
      this.updatePauseControl();
      if (this.manuallyPaused) this.stopAutoplay();
      else this.startAutoplay();
    }

    updatePauseControl() {
      if (!this.pauseButton) return;
      const label = this.manuallyPaused ? 'Play' : 'Pause';
      this.pauseButton.setAttribute('aria-pressed', String(this.manuallyPaused));
      this.pauseButton.setAttribute('aria-label', `${label} campaign carousel`);
      if (this.pauseLabel) this.pauseLabel.textContent = label;
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.manuallyPaused ||
        this.hoverPaused ||
        this.focusPaused ||
        document.hidden ||
        this.prefersReducedMotion()
      ) return;

      this.stopAutoplay();
      this.autoplayTimer = window.setTimeout(() => this.setActive(this.activeIndex + 1), this.duration);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    prefersReducedMotion() {
      return this.motionQuery?.matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    trackActiveSlide() {
      if (document.hidden || !this.isVisible) return;
      const slide = this.slides[this.activeIndex];
      if (!slide) return;

      const key = `${this.dataset.sectionId}:${slide.dataset.slideId}`;
      if (viewedSlides.has(key)) return;
      if (!this.hasPromotionMetadata(slide)) return;

      viewedSlides.add(key);
      this.pushPromotion('view_promotion', slide);
    }

    hasPromotionMetadata(slide) {
      return ['promotionId', 'promotionName', 'creativeName', 'creativeSlot'].some((key) => Boolean(slide.dataset[key]));
    }

    pushPromotion(event, slide, destination = '') {
      if (!this.hasPromotionMetadata(slide)) return;

      window.dataLayer = window.dataLayer || [];
      const payload = {
        event,
        ecommerce: {
          items: [
            {
              promotion_id: slide.dataset.promotionId || '',
              promotion_name: slide.dataset.promotionName || '',
              creative_name: slide.dataset.creativeName || '',
              creative_slot: slide.dataset.creativeSlot || ''
            }
          ]
        }
      };
      if (destination) payload.promotion_destination = destination;
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(payload);
    }
  }

  if (!customElements.get('diantonio-campaign-hero')) {
    customElements.define('diantonio-campaign-hero', DiAntonioCampaignHero);
  }

  if (!window.__diAntonioCampaignHeroSectionLoadListener) {
    window.__diAntonioCampaignHeroSectionLoadListener = true;
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('diantonio-campaign-hero').forEach((hero) => hero.initialize());
    });
  }
})();
